import { PATH } from 'constants/path';
import { getCredentialingOverview } from 'api/credentialing';
import { getInsuranceBillingStatus } from 'api/insuranceBilling';
import { getHomeInfo } from 'api/provider';
import { parseHelpTypes, isCredentialingHelpType, hasIntent, EHelpType, INTENT_ORDER } from './helpType';
import { isPPAOnboardingCompleted } from 'utils/common';

const FULLSCREEN_ONBOARDING_PATHS: string[] = [
    PATH.CREDENTIALING_LANDING,
    PATH.INSURANCE_BILLING_LANDING,
    PATH.PPA_ONBOARDING_STEPS,
];

// 不看 lead form intent，按 credentialing → billing → PPA 优先级返回第一个已完成 onboarding 的 dashboard 页；
// 都未完成返回 null（接口失败视为未完成）
async function getCompletedOnboardingDashboard(): Promise<string | null> {
    const [credentialing, billing, ppa] = await Promise.all([
        getCredentialingOverview()
            .then((res) =>
                (res?.data?.data?.providers || []).some(
                    (p: { onboardingStatus?: string }) => p.onboardingStatus === 'COMPLETED',
                ),
            )
            .catch(() => false),
        getInsuranceBillingStatus()
            .then((res) => res?.data?.data?.onboardingCompleted === true)
            .catch(() => false),
        getHomeInfo()
            .then((res) => isPPAOnboardingCompleted(res?.data?.data))
            .catch(() => false),
    ]);
    if (credentialing) return `${PATH.DASHBOARD}/${PATH.CREDENTIALING}`;
    if (billing) return `${PATH.DASHBOARD}/${PATH.INSURANCE_BILLING}`;
    if (ppa) return PATH.DASHBOARD;
    return null;
}

// 按优先级找到第一个未完成的 onboarding，返回其 landing 页；
// 全部完成则按最高优先级 intent 返回 dashboard 子页面。
async function resolveIntentTarget(practiceHelpType?: string): Promise<string> {
    const intents = parseHelpTypes(practiceHelpType);
    if (intents.length === 0) {
        return PATH.DASHBOARD;
    }

    const hasCredentialing = isCredentialingHelpType(practiceHelpType);
    const hasBilling = hasIntent(practiceHelpType, EHelpType.INSURANCE_BILLING);
    const hasPatientGrowth = hasIntent(practiceHelpType, EHelpType.PATIENT_GROWTH);

    // 1. Credentialing（最高优先级）
    if (hasCredentialing) {
        try {
            const overviewRes = await getCredentialingOverview();
            const providers = overviewRes?.data?.data?.providers || [];
            const done = providers.some((p: { onboardingStatus?: string }) => p.onboardingStatus === 'COMPLETED');
            if (!done) {
                return PATH.CREDENTIALING_LANDING;
            }
        } catch {
            return PATH.CREDENTIALING_LANDING;
        }
    }

    // 2. Insurance billing
    if (hasBilling) {
        try {
            const statusRes = await getInsuranceBillingStatus();
            const done = statusRes?.data?.data?.onboardingCompleted === true;
            if (!done) {
                return PATH.INSURANCE_BILLING_LANDING;
            }
        } catch {
            return PATH.INSURANCE_BILLING_LANDING;
        }
    }

    // 3. Patient growth (PPA onboarding)
    if (hasPatientGrowth) {
        try {
            const homeRes = await getHomeInfo();
            if (!isPPAOnboardingCompleted(homeRes?.data?.data)) {
                return PATH.PPA_ONBOARDING_STEPS;
            }
        } catch {
            return PATH.PPA_ONBOARDING_STEPS;
        }
    }

    // 4. 全部完成，按最高优先级 intent 决定 dashboard 落地页
    const DASHBOARD_MAP: Record<string, string> = {
        [EHelpType.CREDENTIALING]: `${PATH.DASHBOARD}/${PATH.CREDENTIALING}`,
        [EHelpType.INSURANCE_BILLING]: `${PATH.DASHBOARD}/${PATH.INSURANCE_BILLING}`,
        [EHelpType.PATIENT_GROWTH]: PATH.DASHBOARD,
        [EHelpType.EHR]: PATH.DASHBOARD,
    };

    for (const intent of INTENT_ORDER) {
        if (intents.includes(intent) && DASHBOARD_MAP[intent]) {
            return DASHBOARD_MAP[intent];
        }
    }

    return PATH.DASHBOARD;
}

// 目标是全屏 onboarding 页时，若已完成过任意一种 onboarding，改去已完成功能的 dashboard（带左侧菜单），
// 避免已在使用其他功能的 provider 登录后被困在全屏 onboarding 页；未完成的流程从菜单进入。
export async function getIntentRedirect(practiceHelpType?: string): Promise<string> {
    const target = await resolveIntentTarget(practiceHelpType);
    if (FULLSCREEN_ONBOARDING_PATHS.includes(target)) {
        const completedDashboard = await getCompletedOnboardingDashboard();
        if (completedDashboard) {
            return completedDashboard;
        }
    }
    return target;
}
